import React, { useState } from 'react';
import { ChefHat, X, Sparkles, Clock, Users, ChevronDown, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const CUISINES = ['Indian', 'Fusion', 'Mexican', 'Asian', 'Italian', 'American', 'Mediterranean'] as const;
type Cuisine = (typeof CUISINES)[number];
interface GeneratedRecipe {
  name: string;
  description: string;
  cookTime: number;
  servings: number;
  ingredients: {
    item: string;
    quantity: string;
  }[];
  steps: string[];
  tips: string;
  cuisine: Cuisine;
}
// Template-based recipe generation
function generateRecipe(ingredients: string[], cuisine: Cuisine): GeneratedRecipe {
  const mainIngredient = ingredients[0] || 'vegetables';
  const secondary = ingredients.slice(1, 3).join(' and ') || 'spices';
  const templates: Record<Cuisine, (main: string, sec: string, ings: string[]) => GeneratedRecipe> = {
    Indian: (main, sec, ings) => ({
      name: `${capitalize(main)} ${pickOne(['Masala', 'Sabzi', 'Curry', 'Bhaji'])}`,
      description: `A fragrant, spiced Indian dish featuring ${main} cooked with ${sec} in a rich tomato-onion gravy. Perfect with roti or rice.`,
      cookTime: 25,
      servings: 3,
      cuisine: 'Indian',
      ingredients: [{
        item: capitalize(main),
        quantity: '200g'
      }, {
        item: 'Onion',
        quantity: '2 medium, finely chopped'
      }, {
        item: 'Tomato',
        quantity: '2 medium, pureed'
      }, {
        item: 'Ginger-garlic paste',
        quantity: '1 tsp'
      }, {
        item: 'Cumin seeds',
        quantity: '1 tsp'
      }, {
        item: 'Turmeric powder',
        quantity: '½ tsp'
      }, {
        item: 'Coriander powder',
        quantity: '1 tsp'
      }, {
        item: 'Garam masala',
        quantity: '½ tsp'
      }, {
        item: 'Oil',
        quantity: '2 tbsp'
      }, {
        item: 'Salt',
        quantity: 'to taste'
      }, {
        item: 'Fresh coriander',
        quantity: 'for garnish'
      }, ...ings.slice(1).map(i => ({
        item: capitalize(i),
        quantity: 'as needed'
      }))],
      steps: [`Heat oil in a pan over medium heat. Add cumin seeds and let them splutter.`, `Add chopped onions and sauté until golden brown, about 5-7 minutes.`, `Add ginger-garlic paste and cook for 1 minute until fragrant.`, `Add pureed tomatoes, turmeric, coriander powder, and salt. Cook until oil separates, about 5 minutes.`, `Add ${main} and mix well to coat with the masala. Add ¼ cup water if needed.`, `Cover and cook on low heat for 10-12 minutes until ${main} is tender.`, `Sprinkle garam masala, stir gently, and cook for 2 more minutes.`, `Garnish with fresh coriander and serve hot with roti or steamed rice.`],
      tips: `For extra richness, add 2 tbsp of cream or yogurt at the end. This dish tastes even better the next day as the flavors develop.`
    }),
    Fusion: (main, sec, ings) => ({
      name: `${capitalize(main)} Fusion ${pickOne(['Bowl', 'Wrap', 'Bites', 'Rolls'])}`,
      description: `A creative fusion dish combining ${main} with global flavors — Indian spices meet international techniques for a truly unique experience.`,
      cookTime: 20,
      servings: 2,
      cuisine: 'Fusion',
      ingredients: [{
        item: capitalize(main),
        quantity: '200g'
      }, {
        item: 'Garlic',
        quantity: '3 cloves, minced'
      }, {
        item: 'Soy sauce',
        quantity: '2 tbsp'
      }, {
        item: 'Chaat masala',
        quantity: '1 tsp'
      }, {
        item: 'Lemon juice',
        quantity: '1 tbsp'
      }, {
        item: 'Olive oil',
        quantity: '2 tbsp'
      }, {
        item: 'Mixed herbs',
        quantity: '1 tsp'
      }, {
        item: 'Cheese',
        quantity: '50g, grated'
      }, {
        item: 'Salt & pepper',
        quantity: 'to taste'
      }, ...ings.slice(1).map(i => ({
        item: capitalize(i),
        quantity: 'as needed'
      }))],
      steps: [`Prepare ${main} by cutting into bite-sized pieces. Marinate with soy sauce, lemon juice, and chaat masala for 10 minutes.`, `Heat olive oil in a pan over high heat.`, `Add garlic and sauté for 30 seconds until fragrant.`, `Add marinated ${main} and cook on high heat for 5-7 minutes, stirring occasionally.`, `Season with mixed herbs, salt, and pepper.`, `Top with grated cheese and let it melt slightly.`, `Serve immediately in a bowl or wrap, garnished with fresh herbs.`],
      tips: `The key to this fusion dish is the high-heat cooking that creates a slight char. Don't overcrowd the pan!`
    }),
    Mexican: (main, sec, ings) => ({
      name: `${capitalize(main)} Mexican ${pickOne(['Tacos', 'Bowl', 'Quesadilla', 'Burrito'])}`,
      description: `Vibrant Mexican-inspired dish with ${main}, smoky spices, and fresh toppings. Zesty, colorful, and satisfying.`,
      cookTime: 20,
      servings: 2,
      cuisine: 'Mexican',
      ingredients: [{
        item: capitalize(main),
        quantity: '200g'
      }, {
        item: 'Cumin powder',
        quantity: '1 tsp'
      }, {
        item: 'Smoked paprika',
        quantity: '1 tsp'
      }, {
        item: 'Lime juice',
        quantity: '2 tbsp'
      }, {
        item: 'Garlic',
        quantity: '2 cloves'
      }, {
        item: 'Corn',
        quantity: '½ cup'
      }, {
        item: 'Black beans',
        quantity: '½ cup, cooked'
      }, {
        item: 'Avocado',
        quantity: '1, sliced'
      }, {
        item: 'Tortillas or rice',
        quantity: 'for serving'
      }, {
        item: 'Fresh cilantro',
        quantity: 'for garnish'
      }, ...ings.slice(1).map(i => ({
        item: capitalize(i),
        quantity: 'as needed'
      }))],
      steps: [`Season ${main} with cumin, smoked paprika, garlic, salt, and half the lime juice.`, `Heat a pan over medium-high heat with a little oil.`, `Cook seasoned ${main} for 8-10 minutes until cooked through and slightly charred.`, `Warm tortillas or cook rice as your base.`, `Layer: base → ${main} → black beans → corn → avocado slices.`, `Drizzle remaining lime juice over the top.`, `Garnish with fresh cilantro and serve immediately.`],
      tips: `Add a dollop of sour cream or Greek yogurt for creaminess. A pinch of chipotle powder adds authentic smokiness.`
    }),
    Asian: (main, sec, ings) => ({
      name: `${capitalize(main)} Asian Stir Fry`,
      description: `Quick and flavorful Asian stir fry with ${main} in a savory umami sauce. Ready in under 20 minutes!`,
      cookTime: 15,
      servings: 2,
      cuisine: 'Asian',
      ingredients: [{
        item: capitalize(main),
        quantity: '200g'
      }, {
        item: 'Soy sauce',
        quantity: '3 tbsp'
      }, {
        item: 'Sesame oil',
        quantity: '1 tsp'
      }, {
        item: 'Ginger',
        quantity: '1 inch, grated'
      }, {
        item: 'Garlic',
        quantity: '3 cloves, minced'
      }, {
        item: 'Spring onions',
        quantity: '3, sliced'
      }, {
        item: 'Cornflour',
        quantity: '1 tsp (for sauce)'
      }, {
        item: 'Vegetable oil',
        quantity: '2 tbsp'
      }, {
        item: 'Sesame seeds',
        quantity: 'for garnish'
      }, ...ings.slice(1).map(i => ({
        item: capitalize(i),
        quantity: 'as needed'
      }))],
      steps: [`Mix soy sauce, sesame oil, and cornflour in a small bowl to make the sauce. Set aside.`, `Heat vegetable oil in a wok or large pan over very high heat.`, `Add ginger and garlic, stir fry for 30 seconds.`, `Add ${main} and stir fry on high heat for 5-6 minutes.`, `Pour the sauce over and toss everything together for 2 minutes.`, `Add spring onions and toss once more.`, `Garnish with sesame seeds and serve immediately over steamed rice or noodles.`],
      tips: `High heat is the secret to a good stir fry. Have all ingredients prepped before you start cooking — it moves fast!`
    }),
    Italian: (main, sec, ings) => ({
      name: `${capitalize(main)} Italian ${pickOne(['Pasta', 'Risotto', 'Bruschetta', 'Frittata'])}`,
      description: `Classic Italian preparation with ${main}, featuring simple, quality ingredients that let the flavors shine.`,
      cookTime: 30,
      servings: 2,
      cuisine: 'Italian',
      ingredients: [{
        item: capitalize(main),
        quantity: '200g'
      }, {
        item: 'Pasta or arborio rice',
        quantity: '200g'
      }, {
        item: 'Olive oil',
        quantity: '3 tbsp, extra virgin'
      }, {
        item: 'Garlic',
        quantity: '4 cloves'
      }, {
        item: 'Cherry tomatoes',
        quantity: '1 cup'
      }, {
        item: 'Fresh basil',
        quantity: 'handful'
      }, {
        item: 'Parmesan cheese',
        quantity: '50g, grated'
      }, {
        item: 'Salt & black pepper',
        quantity: 'to taste'
      }, ...ings.slice(1).map(i => ({
        item: capitalize(i),
        quantity: 'as needed'
      }))],
      steps: [`Cook pasta in well-salted boiling water until al dente. Reserve 1 cup pasta water before draining.`, `Heat olive oil in a large pan over medium heat. Add garlic and cook until golden.`, `Add cherry tomatoes and cook until they burst and form a sauce, about 5 minutes.`, `Add ${main} and cook for 5-7 minutes until tender.`, `Add drained pasta and toss with a splash of pasta water to create a silky sauce.`, `Remove from heat, add fresh basil and half the parmesan. Toss well.`, `Serve immediately topped with remaining parmesan and a drizzle of olive oil.`],
      tips: `Never rinse pasta after cooking — the starch helps the sauce cling. Use the best olive oil you have for finishing.`
    }),
    American: (main, sec, ings) => ({
      name: `${capitalize(main)} American ${pickOne(['Burger', 'Casserole', 'Skillet', 'Sandwich'])}`,
      description: `Hearty American comfort food featuring ${main} with bold flavors and satisfying textures.`,
      cookTime: 25,
      servings: 2,
      cuisine: 'American',
      ingredients: [{
        item: capitalize(main),
        quantity: '200g'
      }, {
        item: 'Cheddar cheese',
        quantity: '50g, sliced'
      }, {
        item: 'Butter',
        quantity: '2 tbsp'
      }, {
        item: 'Garlic powder',
        quantity: '1 tsp'
      }, {
        item: 'Smoked paprika',
        quantity: '1 tsp'
      }, {
        item: 'Bread or buns',
        quantity: 'for serving'
      }, {
        item: 'Lettuce & tomato',
        quantity: 'for topping'
      }, {
        item: 'Mustard & ketchup',
        quantity: 'to taste'
      }, ...ings.slice(1).map(i => ({
        item: capitalize(i),
        quantity: 'as needed'
      }))],
      steps: [`Season ${main} generously with garlic powder, smoked paprika, salt, and pepper.`, `Heat butter in a cast iron skillet over medium-high heat.`, `Cook ${main} for 5-6 minutes per side until golden and cooked through.`, `Place cheese on top and cover for 1 minute to melt.`, `Toast bread or buns in the same pan with remaining butter.`, `Assemble with lettuce, tomato, and your choice of condiments.`, `Serve immediately with a side of fries or salad.`],
      tips: `Let the ${main} rest for 2 minutes after cooking for juicier results. A cast iron skillet gives the best sear.`
    }),
    Mediterranean: (main, sec, ings) => ({
      name: `${capitalize(main)} Mediterranean ${pickOne(['Salad', 'Bowl', 'Wrap', 'Platter'])}`,
      description: `Light, fresh Mediterranean dish with ${main}, herbs, and a bright lemon-olive oil dressing.`,
      cookTime: 20,
      servings: 2,
      cuisine: 'Mediterranean',
      ingredients: [{
        item: capitalize(main),
        quantity: '200g'
      }, {
        item: 'Lemon juice',
        quantity: '3 tbsp, fresh'
      }, {
        item: 'Olive oil',
        quantity: '3 tbsp, extra virgin'
      }, {
        item: 'Garlic',
        quantity: '2 cloves, minced'
      }, {
        item: 'Fresh parsley',
        quantity: 'handful, chopped'
      }, {
        item: 'Feta cheese',
        quantity: '50g, crumbled'
      }, {
        item: 'Kalamata olives',
        quantity: '¼ cup'
      }, {
        item: 'Cucumber',
        quantity: '1, diced'
      }, {
        item: 'Cherry tomatoes',
        quantity: '1 cup, halved'
      }, ...ings.slice(1).map(i => ({
        item: capitalize(i),
        quantity: 'as needed'
      }))],
      steps: [`Prepare ${main} by cooking or marinating as appropriate.`, `Whisk together lemon juice, olive oil, garlic, salt, and pepper to make dressing.`, `Combine cucumber, cherry tomatoes, and olives in a large bowl.`, `Add cooked ${main} to the bowl.`, `Pour dressing over and toss gently to combine.`, `Top with crumbled feta and fresh parsley.`, `Serve immediately or refrigerate for 30 minutes for deeper flavor.`],
      tips: `This dish is best with room-temperature ingredients. The lemon dressing can be made ahead and stored for 3 days.`
    })
  };
  return templates[cuisine](mainIngredient, secondary, ingredients);
}
function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
function pickOne<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
export function RecipeGenerator() {
  const [ingredientInput, setIngredientInput] = useState('');
  const [ingredientTags, setIngredientTags] = useState<string[]>([]);
  const [cuisine, setCuisine] = useState<Cuisine>('Indian');
  const [generated, setGenerated] = useState<GeneratedRecipe | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const addTag = (val: string) => {
    const trimmed = val.trim().toLowerCase();
    if (trimmed && !ingredientTags.includes(trimmed)) setIngredientTags(prev => [...prev, trimmed]);
    setIngredientInput('');
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(ingredientInput);
    }
  };
  const removeTag = (tag: string) => setIngredientTags(prev => prev.filter(t => t !== tag));
  const handleGenerate = () => {
    if (ingredientTags.length === 0) return;
    setIsGenerating(true);
    setGenerated(null);
    // Simulate a brief "thinking" delay for UX delight
    setTimeout(() => {
      setGenerated(generateRecipe(ingredientTags, cuisine));
      setIsGenerating(false);
    }, 1200);
  };
  const handleRegenerate = () => {
    if (ingredientTags.length === 0) return;
    setIsGenerating(true);
    setTimeout(() => {
      setGenerated(generateRecipe(ingredientTags, cuisine));
      setIsGenerating(false);
    }, 800);
  };
  return <section id="generator" className="py-20 px-4 bg-white" data-id="element-150">
      <div className="max-w-4xl mx-auto" data-id="element-151">
        <div className="text-center mb-12" data-id="element-152">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-100 text-orange-700 font-semibold text-sm mb-4" data-id="element-153">
            <Sparkles size={16} data-id="element-154" /> AI-Style Recipe Generator
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-stone-900 mb-4" style={{
          fontFamily: 'Poppins, sans-serif'
        }} data-id="element-155">
            ✨ Generate Your Recipe
          </h2>
          <p className="text-stone-500 text-lg max-w-xl mx-auto" data-id="element-156">
            Tell us your ingredients and cuisine preference — we'll craft a
            custom recipe just for you.
          </p>
        </div>

        {/* Input Card */}
        <div className="bg-gradient-to-br from-orange-50 to-green-50 rounded-3xl p-6 md:p-8 border border-stone-100 shadow-xl mb-8" data-id="element-157">
          <div className="space-y-5" data-id="element-158">
            {/* Ingredients */}
            <div data-id="element-159">
              <label className="block text-sm font-bold text-stone-700 mb-2" data-id="element-160">
                🥕 Your Ingredients
                <span className="font-normal text-stone-400 ml-2" data-id="element-161">
                  Press Enter or comma to add (at least 1)
                </span>
              </label>
              <div className="flex flex-wrap gap-2 p-3 border border-stone-200 rounded-xl focus-within:border-orange-400 focus-within:ring-2 focus-within:ring-orange-100 min-h-[52px] bg-white" data-id="element-162">
                {ingredientTags.map(tag => <span key={tag} className="flex items-center gap-1 bg-orange-100 text-orange-800 text-sm font-semibold px-3 py-1 rounded-full" data-id="element-163">
                    {tag}
                    <button onClick={() => removeTag(tag)} className="hover:text-red-500 transition-colors" data-id="element-164">
                      <X size={14} data-id="element-165" />
                    </button>
                  </span>)}
                <input type="text" value={ingredientInput} onChange={e => setIngredientInput(e.target.value)} onKeyDown={handleKeyDown} onBlur={() => ingredientInput.trim() && addTag(ingredientInput)} placeholder={ingredientTags.length === 0 ? 'e.g. paneer, spinach, mushroom...' : 'Add more...'} className="flex-1 min-w-[120px] outline-none bg-transparent text-stone-700 placeholder-stone-400 text-sm" data-id="element-166" />
              </div>
            </div>

            {/* Cuisine */}
            <div data-id="element-167">
              <label className="block text-sm font-bold text-stone-700 mb-2" data-id="element-168">
                🌍 Choose Cuisine Style
              </label>
              <div className="flex flex-wrap gap-2" data-id="element-169">
                {CUISINES.map(c => <button key={c} onClick={() => setCuisine(c)} className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${cuisine === c ? 'bg-orange-500 text-white shadow-md scale-105' : 'bg-white text-stone-600 border border-stone-200 hover:border-orange-300'}`} data-id="element-170">
                    {c}
                  </button>)}
              </div>
            </div>

            {/* Generate Button */}
            <button onClick={handleGenerate} disabled={ingredientTags.length === 0 || isGenerating} className="w-full flex items-center justify-center gap-3 bg-orange-500 hover:bg-orange-600 disabled:bg-stone-300 disabled:cursor-not-allowed text-white font-bold py-4 rounded-xl transition-all shadow-lg shadow-orange-500/20 text-lg" data-id="element-171">
              {isGenerating ? <>
                  <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 1,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-172">
                    <ChefHat size={22} data-id="element-173" />
                  </motion.div>
                  Crafting your recipe...
                </> : <>
                  <Sparkles size={22} data-id="element-174" /> Generate My Recipe
                </>}
            </button>
          </div>
        </div>

        {/* Generated Recipe Output */}
        <AnimatePresence data-id="element-175">
          {generated && !isGenerating && <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.5
        }} className="bg-white rounded-3xl shadow-2xl border border-stone-100 overflow-hidden" data-id="element-176">
              {/* Recipe Header */}
              <div className="bg-gradient-to-r from-green-600 to-green-500 p-8 text-white" data-id="element-177">
                <div className="flex items-start justify-between gap-4" data-id="element-178">
                  <div data-id="element-179">
                    <div className="flex items-center gap-2 mb-2" data-id="element-180">
                      <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide" data-id="element-181">
                        {generated.cuisine} Cuisine
                      </span>
                      <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-182">
                        ✨ Custom Recipe
                      </span>
                    </div>
                    <h3 className="text-3xl font-bold mb-2" style={{
                  fontFamily: 'Poppins, sans-serif'
                }} data-id="element-183">
                      {generated.name}
                    </h3>
                    <p className="text-green-100 leading-relaxed" data-id="element-184">
                      {generated.description}
                    </p>
                  </div>
                  <button onClick={handleRegenerate} className="flex-shrink-0 bg-white/20 hover:bg-white/30 p-2.5 rounded-xl transition-colors" title="Generate another variation" data-id="element-185">
                    <RefreshCw size={18} data-id="element-186" />
                  </button>
                </div>

                <div className="flex gap-6 mt-6" data-id="element-187">
                  <div className="flex items-center gap-2" data-id="element-188">
                    <Clock size={18} className="text-green-200" data-id="element-189" />
                    <div data-id="element-190">
                      <p className="text-xs text-green-200" data-id="element-191">Cook Time</p>
                      <p className="font-bold" data-id="element-192">{generated.cookTime} mins</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-193">
                    <Users size={18} className="text-green-200" data-id="element-194" />
                    <div data-id="element-195">
                      <p className="text-xs text-green-200" data-id="element-196">Serves</p>
                      <p className="font-bold" data-id="element-197">{generated.servings} people</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-198">
                    <ChefHat size={18} className="text-green-200" data-id="element-199" />
                    <div data-id="element-200">
                      <p className="text-xs text-green-200" data-id="element-201">Difficulty</p>
                      <p className="font-bold" data-id="element-202">Easy</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-stone-100" data-id="element-203">
                {/* Ingredients */}
                <div className="p-8" data-id="element-204">
                  <h4 className="text-lg font-bold text-stone-900 mb-4 flex items-center gap-2" style={{
                fontFamily: 'Poppins, sans-serif'
              }} data-id="element-205">
                    🛒 Ingredients
                  </h4>
                  <ul className="space-y-2.5" data-id="element-206">
                    {generated.ingredients.map((ing, i) => <motion.li key={i} initial={{
                  opacity: 0,
                  x: -10
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: i * 0.05
                }} className="flex items-start gap-3" data-id="element-207">
                        <span className="w-2 h-2 rounded-full bg-green-500 mt-2 flex-shrink-0" data-id="element-208" />
                        <span className="text-stone-700" data-id="element-209">
                          <span className="font-semibold" data-id="element-210">{ing.item}</span>
                          <span className="text-stone-400" data-id="element-211">
                            {' '}
                            — {ing.quantity}
                          </span>
                        </span>
                      </motion.li>)}
                  </ul>
                </div>

                {/* Steps */}
                <div className="p-8" data-id="element-212">
                  <h4 className="text-lg font-bold text-stone-900 mb-4 flex items-center gap-2" style={{
                fontFamily: 'Poppins, sans-serif'
              }} data-id="element-213">
                    👨‍🍳 Method
                  </h4>
                  <ol className="space-y-4" data-id="element-214">
                    {generated.steps.map((step, i) => <motion.li key={i} initial={{
                  opacity: 0,
                  x: 10
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: i * 0.07
                }} className="flex gap-3" data-id="element-215">
                        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-green-100 text-green-700 font-bold text-sm flex items-center justify-center" data-id="element-216">
                          {i + 1}
                        </span>
                        <p className="text-stone-600 text-sm leading-relaxed pt-0.5" data-id="element-217">
                          {step}
                        </p>
                      </motion.li>)}
                  </ol>
                </div>
              </div>

              {/* Chef's Tip */}
              <div className="px-8 pb-8" data-id="element-218">
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex gap-3" data-id="element-219">
                  <span className="text-2xl flex-shrink-0" data-id="element-220">💡</span>
                  <div data-id="element-221">
                    <p className="font-bold text-amber-900 text-sm mb-1" data-id="element-222">
                      Chef's Tip
                    </p>
                    <p className="text-amber-800 text-sm" data-id="element-223">{generated.tips}</p>
                  </div>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}